/**
 * Centralized permission configuration.
 *
 * Update special user lists here instead of scattering login-id checks
 * across routes, menus, and pages.
 */

export const PERMISSIONS = {
    ADMIN_ACCESS: 'adminAccess',
    CHANNEL_ADMIN: 'channelAdmin',
    COMMISSION_ACCESS: 'commissionAccess',
    EXPENSES_ACCESS: 'expensesAccess',
    FACTURE_EXPORT: 'factureExport',
    CALENDAR_ADMIN_MODE: 'calendarAdminMode',
};

export const VISIBILITY_RULES = {
    STAFF_ARTIST: 'staff_artist',
    COMMISSION_ACCESS: 'can_access_commission',
};

export const SPECIAL_USERS = {
    [PERMISSIONS.ADMIN_ACCESS]: ['chan69x'],
    [PERMISSIONS.CHANNEL_ADMIN]: ['chan69x'],
    [PERMISSIONS.EXPENSES_ACCESS]: ['chan69x'],
    [PERMISSIONS.FACTURE_EXPORT]: ['chan69x'],
    [PERMISSIONS.CALENDAR_ADMIN_MODE]: ['chan69x'],
};

const LEGACY_ALLOWED_USER_MAP = {
    chan69x_user: PERMISSIONS.CHANNEL_ADMIN,
};

const KNOWN_PERMISSION_KEYS = new Set(Object.values(PERMISSIONS));

/**
 * 사용자 레벨 가져오기 (다양한 필드명 대응)
 */
export const getUserLevel = (userInfo) => {
    return userInfo?.level || userInfo?.user_level || userInfo?.userLevel;
};

/**
 * 사용자 로그인 ID 가져오기
 */
export const getUserLoginId = (userInfo) => {
    return userInfo?.user_login_id;
};

/**
 * can_access_commission 기반 접근 권한
 */
export const hasCommissionAccess = (userInfo) => {
    return userInfo?.can_access_commission === true;
};

/**
 * 특정 권한 그룹에 속하는지 체크
 */
export const hasPermission = (userInfo, permissionKey) => {
    if (!permissionKey || !SPECIAL_USERS[permissionKey]) {
        return false;
    }

    const userLoginId = getUserLoginId(userInfo);
    return SPECIAL_USERS[permissionKey].includes(userLoginId);
};

const matchesPermissionRule = (userInfo, permissionKey) => {
    if (permissionKey === PERMISSIONS.COMMISSION_ACCESS) {
        return hasCommissionAccess(userInfo);
    }

    return hasPermission(userInfo, permissionKey);
};

/**
 * 어드민 또는 특수 권한 사용자인지 체크
 */
export const hasAdminAccess = (userInfo) => {
    const userLevel = getUserLevel(userInfo);
    return userLevel === 'admin' || hasPermission(userInfo, PERMISSIONS.ADMIN_ACCESS);
};

/**
 * 채널 관리자 권한
 */
export const isChannelAdmin = (userInfo) => {
    return hasPermission(userInfo, PERMISSIONS.CHANNEL_ADMIN);
};

/**
 * 지출 관리 접근 권한
 */
export const hasExpensesAccess = (userInfo) => {
    return matchesPermissionRule(userInfo, PERMISSIONS.EXPENSES_ACCESS);
};

/**
 * Facture PDF 다운로드 권한
 */
export const canDownloadFacture = (userInfo) => {
    return matchesPermissionRule(userInfo, PERMISSIONS.FACTURE_EXPORT);
};

/**
 * 캘린더 ADMIN/BIDON 모드 사용 권한
 */
export const canUseCalendarAdminMode = (userInfo) => {
    return matchesPermissionRule(userInfo, PERMISSIONS.CALENDAR_ADMIN_MODE);
};

/**
 * legacy allowedUsers 값을 현재 권한 키로 변환
 */
export const resolveAllowedPermissions = (allowedPermissions = [], allowedUsers = []) => {
    const normalizedPermissions = Array.isArray(allowedPermissions) ? allowedPermissions : [];
    const normalizedUsers = Array.isArray(allowedUsers) ? allowedUsers : [];

    const legacyPermissions = normalizedUsers
        .map((allowedUserKey) => LEGACY_ALLOWED_USER_MAP[allowedUserKey])
        .filter(Boolean);

    return Array.from(new Set([
        ...normalizedPermissions,
        ...legacyPermissions,
    ]));
};

/**
 * ProtectedRoute에서 사용: 레벨 또는 권한 체크
 */
export const checkAccess = (userInfo, allowedLevels = [], allowedPermissions = []) => {
    const userLevel = getUserLevel(userInfo);

    if (allowedLevels.includes(userLevel)) {
        return true;
    }

    for (const permissionKey of allowedPermissions) {
        if (matchesPermissionRule(userInfo, permissionKey)) {
            return true;
        }
    }

    return false;
};

/**
 * 메뉴 아이템 표시 여부 체크
 */
export const checkMenuItemAccess = (item, userInfo, level) => {
    const { showOnlyFor } = item;

    if (!showOnlyFor) {
        return true;
    }

    const requiredPermissions = showOnlyFor.filter((rule) => KNOWN_PERMISSION_KEYS.has(rule));
    if (requiredPermissions.some((permissionKey) => !matchesPermissionRule(userInfo, permissionKey))) {
        return false;
    }

    if (showOnlyFor.includes(VISIBILITY_RULES.STAFF_ARTIST) && level !== VISIBILITY_RULES.STAFF_ARTIST) {
        return false;
    }

    if (showOnlyFor.includes(VISIBILITY_RULES.COMMISSION_ACCESS) && !hasCommissionAccess(userInfo)) {
        return false;
    }

    return true;
};
